import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Pencil, Trash2, Plus, LogOut, Eye, FileText, Users, MessageSquare, Radio, Lock, Mail, CheckCircle2 } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="border p-6 bg-[color:var(--surface)]" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={testid}>
      <Icon className="text-[color:var(--accent)]" size={20} />
      <div className="kicker mt-3 text-[color:var(--text-muted)]">{label}</div>
      <div className="font-serif text-4xl font-bold mt-1">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [articles, setArticles] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState(null);
  const [subs, setSubs] = useState([]);
  const [interviews, setInterviews] = useState([]);

  const load = () => {
    api.get("/articles", { params: { limit: 100 } }).then((r) => setArticles(r.data.items || []));
    api.get("/articles", { params: { limit: 100, published: false } }).then((r) => setDrafts(r.data.items || [])).catch(() => setDrafts([]));
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/newsletter/list").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/interviews/list").then((r) => setInterviews(r.data)).catch(() => {});
  };
  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (loading) return <div className="container-editorial py-24 text-center text-[color:var(--text-muted)]">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const remove = async (slug) => {
    if (!window.confirm("Delete this story?")) return;
    try {
      await api.delete(`/articles/${slug}`);
      toast.success("Deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="container-editorial py-12">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="kicker text-[color:var(--accent)] mb-2">Newsroom</div>
          <h1 className="font-serif text-5xl font-bold tracking-tight">Editor Desk</h1>
          <p className="text-[color:var(--text-muted)] mt-2">Signed in as <span className="font-semibold text-[color:var(--text)]">{user.name}</span></p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/new" className="btn-accent flex items-center gap-2" data-testid="admin-new-story"><Plus size={16} /> New Story</Link>
          <Link to="/admin/about" className="btn-outline flex items-center gap-2" data-testid="admin-edit-about">Edit About</Link>
          <Link to="/admin/site" className="btn-outline flex items-center gap-2" data-testid="admin-edit-site">Site Content</Link>
          <Link to="/admin/categories" className="btn-outline flex items-center gap-2" data-testid="admin-edit-cats">Sections</Link>
          <button onClick={logout} className="btn-outline flex items-center gap-2" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
        </div>
      </div>

      {stats && (
        <div className="grid md:grid-cols-4 lg:grid-cols-7 gap-4 mb-10">
          <Stat icon={FileText} label="Stories" value={stats.total_articles} testid="stat-articles" />
          <Stat icon={Radio} label="Breaking" value={stats.breaking} testid="stat-breaking" />
          <Stat icon={Eye} label="Total reads" value={(stats.total_views || 0).toLocaleString()} testid="stat-total-views" />
          <Stat icon={Users} label="Newsletter" value={stats.newsletter_subscribers} testid="stat-subs" />
          <Stat icon={Lock} label="Paying" value={stats.active_subscribers || 0} testid="stat-paying" />
          <Stat icon={MessageSquare} label="Comments" value={stats.comments} testid="stat-comments" />
          <Stat icon={Mail} label="Interviews" value={stats.interview_requests_new || 0} testid="stat-interviews" />
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          {drafts.length > 0 && (
            <div className="mb-10 border-l-4 pl-4" style={{ borderColor: "var(--accent)" }} data-testid="admin-drafts-panel">
              <div className="kicker text-[color:var(--accent)] mb-2">Reserved for you · {drafts.length} drafts</div>
              <h2 className="font-serif text-3xl font-bold mb-2">Load your stories</h2>
              <p className="text-[color:var(--text-muted)] mb-4 text-sm">These placeholder posts across <strong>Sports</strong>, <strong>Celebrities</strong> and <strong>Dignitaries</strong> are ready for you. Click Edit, drop in your body, upload the cover image/video, toggle Published.</p>
              <div className="space-y-2">
                {drafts.map((d) => (
                  <Link key={d.slug} to={`/admin/edit/${d.slug}`} className="flex items-center justify-between border p-3 hover:bg-[color:var(--muted)] transition-colors" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid={`draft-${d.slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="kicker text-[color:var(--accent)] mb-1">{d.category_name} · Draft</div>
                      <div className="font-serif text-lg font-bold truncate">{d.title}</div>
                    </div>
                    <Pencil size={16} className="ml-3 shrink-0 text-[color:var(--text-muted)]" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          <h2 className="font-serif text-3xl font-bold mb-4">All Stories</h2>
          <div className="border" style={{ borderColor: "hsl(var(--border-hsl))" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[color:var(--surface)] border-b" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                  <th className="kicker text-left px-4 py-3">Title</th>
                  <th className="kicker text-left px-4 py-3">Section</th>
                  <th className="kicker text-left px-4 py-3">Type</th>
                  <th className="kicker text-left px-4 py-3">Views</th>
                  <th className="kicker text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="admin-articles-table">
                {articles.map((a) => (
                  <tr key={a.slug} className="border-b" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                    <td className="px-4 py-3">
                      <Link to={`/article/${a.slug}`} className="font-semibold hover:text-[color:var(--accent)]" data-testid={`admin-row-${a.slug}`}>{a.title}</Link>
                      <div className="text-xs text-[color:var(--text-muted)] mt-1">
                        {a.breaking && <span className="text-[color:var(--accent)]">● BREAKING </span>}
                        {a.featured && <span>★ Featured </span>}
                        {!a.published && <span>· Draft</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 kicker text-[color:var(--text-muted)]">{a.category_name}</td>
                    <td className="px-4 py-3 kicker text-[color:var(--text-muted)]">{a.media_type}</td>
                    <td className="px-4 py-3">{a.views}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/edit/${a.slug}`} className="inline-block p-2 hover:bg-[color:var(--muted)]" data-testid={`edit-${a.slug}`}><Pencil size={16} /></Link>
                      <button onClick={() => remove(a.slug)} className="p-2 hover:bg-[color:var(--muted)] text-[color:var(--accent)]" data-testid={`delete-${a.slug}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
                {articles.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-[color:var(--text-muted)]">No stories yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <aside>
          {interviews.length > 0 && (
            <div className="mb-8" data-testid="admin-interviews">
              <h2 className="font-serif text-3xl font-bold mb-4">Interview requests</h2>
              <div className="space-y-3 max-h-[400px] overflow-auto pr-2">
                {interviews.map((r) => (
                  <div key={r.id} className={`border p-4 ${r.status === "new" ? "border-l-4" : ""}`} style={{ borderColor: r.status === "new" ? "var(--accent)" : "hsl(var(--border-hsl))" }} data-testid={`interview-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-serif text-lg font-bold leading-tight">{r.subject_person}</div>
                      {r.status === "new" && <span className="kicker text-[color:var(--accent)]">NEW</span>}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-2">
                      {r.name} {r.organization && <>· {r.organization}</>} · <a href={`mailto:${r.email}`} className="hover-underline">{r.email}</a>
                    </div>
                    <p className="text-sm line-clamp-3">{r.message}</p>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="kicker text-[color:var(--text-muted)]">{r.interview_type}</span>
                      {r.proposed_date && <span className="kicker text-[color:var(--text-muted)]">· {r.proposed_date}</span>}
                      {r.status !== "seen" && r.status === "new" && (
                        <button
                          onClick={async () => {
                            await api.put(`/interviews/${r.id}/status?status=seen`);
                            load();
                          }}
                          className="ml-auto text-[color:var(--accent)] hover-underline inline-flex items-center gap-1"
                          data-testid={`interview-seen-${r.id}`}
                        >
                          <CheckCircle2 size={12} /> Mark seen
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h2 className="font-serif text-3xl font-bold mb-4">Newsletter</h2>
          <div className="border p-4 max-h-[500px] overflow-auto" style={{ borderColor: "hsl(var(--border-hsl))" }} data-testid="admin-subs">
            {subs.length === 0 && <p className="text-[color:var(--text-muted)]">No subscribers yet.</p>}
            <ul className="space-y-2 text-sm">
              {subs.map((s) => (
                <li key={s.id} className="flex justify-between border-b pb-2" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                  <span>{s.email}</span>
                  <span className="text-[color:var(--text-muted)] text-xs">{new Date(s.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>
          {stats?.top_articles && (
            <div className="mt-8">
              <h3 className="font-serif text-2xl font-bold mb-3">Top Read</h3>
              <ol className="space-y-2 text-sm">
                {stats.top_articles.map((t, i) => (
                  <li key={t.slug} className="flex gap-3 border-b pb-2" style={{ borderColor: "hsl(var(--border-hsl))" }}>
                    <span className="font-serif text-2xl font-bold text-[color:var(--accent)] w-6">{i + 1}</span>
                    <Link to={`/article/${t.slug}`} className="flex-1 hover-underline">{t.title}</Link>
                    <span className="text-xs text-[color:var(--text-muted)]"><Eye size={11} className="inline" /> {t.views}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
